import { ReactNode } from 'react';
import { Modal } from './Modal';
import { Button } from './Button';

type Props = {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title?: ReactNode;
  message?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Confirm button style — `danger` for destructive actions (the default). */
  variant?: 'danger' | 'primary';
  /** While true the action is in flight: both buttons are disabled. */
  busy?: boolean;
};

/**
 * A small confirmation dialog built on {@link Modal}, so it STACKS above the modal that opened it
 * (the one behind recedes) instead of interrupting with a blocking `window.confirm`. The caller
 * owns the confirm action and closes the dialog itself.
 */
export const ConfirmDialog = ({
  open,
  onClose,
  onConfirm,
  title = 'Are you sure?',
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  variant = 'danger',
  busy = false,
}: Props) => {
  const heading = <div className="text-[15px] font-semibold text-fg-strong">{title}</div>;
  const footer = (
    <div className="flex items-center justify-end gap-2">
      <Button variant="ghost" onClick={onClose} disabled={busy}>
        {cancelLabel}
      </Button>
      <Button variant={variant} onClick={onConfirm} disabled={busy}>
        {confirmLabel}
      </Button>
    </div>
  );
  return (
    <Modal open={open} onClose={onClose} title={heading} footer={footer} className="max-w-[420px]">
      {message ? <div className="text-[13px] leading-relaxed text-fg-soft">{message}</div> : null}
    </Modal>
  );
};

export default ConfirmDialog;
